/**
 * The daily goal ring (Today `2b`/`2a`, geometry pinned in design README §5).
 *
 * Pure SVG and no library: it is one arc, and the one rule that matters is that it never laps.
 * Overflow is not shown by going round again — the evening state says `+8 min` in the text line
 * instead (README §6). A second lap would turn a goal into a score.
 */

/** 2πr at r = 21, the value the canvas hard-codes. */
export const RING_CIRCUMFERENCE = 131.9;

/**
 * `stroke-dasharray` for the progress arc, capped at one full lap and floored at empty.
 * A zero goal cannot happen through the contract (`daily_minutes` is 1–600) but would divide
 * by zero if it ever did.
 */
export function ringFraction(minutesDone: number, goalMinutes: number): number {
  return goalMinutes > 0 ? Math.min(1, Math.max(0, minutesDone / goalMinutes)) : 0;
}

export function ringDash(minutesDone: number, goalMinutes: number): string {
  const fraction = ringFraction(minutesDone, goalMinutes);
  return `${(fraction * RING_CIRCUMFERENCE).toFixed(1)} ${RING_CIRCUMFERENCE}`;
}

export function GoalRing({
  minutesDone,
  goalMinutes,
  size = 48,
  complete = false,
}: {
  minutesDone: number;
  goalMinutes: number;
  /** 48 on mobile, 56 on desktop. The viewBox is 52 either way. */
  size?: number;
  /**
   * M6a, the evening state (`2c`): the goal is met, so the ring is full and the NUMERAL IS GONE.
   * The mark is centred in it instead, by the caller — a full ring with a number in it is still
   * counting, and the whole point of the evening state is that Today stops asking. The ring
   * itself never changes shape: past the goal it does not lap, and the overflow is the text
   * line's "+8 min", not another arc.
   */
  complete?: boolean;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 52 52"
      role="img"
      aria-label={`${minutesDone} of ${goalMinutes} minutes today`}
      style={{ flexShrink: 0 }}
    >
      <circle cx="26" cy="26" r="21" fill="none" stroke="var(--nn-hairline)" strokeWidth="4" />
      {/* Omitted entirely at zero rather than drawn with a zero-length dash: `linecap: round`
          paints a round cap even on a 0-length segment, and that dot at 12 o'clock reads as a
          sliver of progress. Nothing done should look like nothing done. */}
      {ringFraction(minutesDone, goalMinutes) > 0 && (
        <circle
          cx="26"
          cy="26"
          r="21"
          fill="none"
          stroke="var(--nn-accent)"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={ringDash(minutesDone, goalMinutes)}
          transform="rotate(-90 26 26)"
        />
      )}
      {!complete && (
        <text
          x="26"
          y="30"
          textAnchor="middle"
          fontSize="12.5"
          fontWeight="600"
          fill="var(--nn-text)"
        >
          {minutesDone}
        </text>
      )}
    </svg>
  );
}
